import { useEffect, useState } from 'react';
import {
  FileText, FileSpreadsheet, Mail, Eye, Download, Loader2, History, CheckCircle2, AlertTriangle, X, Send, Search, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { adminAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

const fmtR = (n) => `R${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg' }); }
  catch { return String(iso).slice(0, 10); }
};
const fmtDT = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' }); }
  catch { return String(iso).slice(0, 16); }
};

export default function AdminPurchaseOrders() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // supplier_id -> array of out-of-window order ids the admin chose to add to the PO
  const [poIncludes, setPoIncludes] = useState({});
  const [previewRow, setPreviewRow] = useState(null); // the supplier row the preview was opened from
  const [sendDialog, setSendDialog] = useState(null); // {supplier_id, supplier_name, to, subject, message, mark_orders_processing}
  const [sending, setSending] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(null); // {supplier_id, supplier_name, items}
  const [resendDialog, setResendDialog] = useState(null); // {po_id, po_number, to, cc, subject, message}
  const [resending, setResending] = useState(false);
  const [diagQuery, setDiagQuery] = useState('');
  const [diagResult, setDiagResult] = useState(null); // {loading} | data | {error}
  const [alert, setAlert] = useState(null); // out-of-window uncovered summary
  const [alertEnabled, setAlertEnabled] = useState(() => localStorage.getItem('po_oow_alert_enabled') !== 'false');

  const runDiagnose = async () => {
    const q = diagQuery.trim();
    if (!q) return;
    setDiagResult({ loading: true });
    try {
      const r = await adminAPI.diagnoseOrderPO(q);
      setDiagResult(r.data);
    } catch (e) {
      setDiagResult({ error: e?.response?.data?.detail || 'Order not found' });
    }
  };

  const toggleAlert = () => {
    setAlertEnabled((v) => {
      const next = !v;
      localStorage.setItem('po_oow_alert_enabled', next ? 'true' : 'false');
      return next;
    });
  };

  const fetchRows = () => {
    setLoading(true);
    adminAPI.listPOSuppliers()
      .then((r) => setRows(r.data))
      .catch(() => toast.error('Failed to load suppliers'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchRows(); }, []);

  useEffect(() => {
    adminAPI.poOutOfWindowAlert()
      .then((r) => setAlert(r.data))
      .catch(() => setAlert(null));
  }, []);

  const loadPreview = async (row, includeIds) => {
    const [r, u] = await Promise.all([
      adminAPI.previewPO(row.supplier_id, undefined, includeIds),
      adminAPI.getUncoveredOrders(row.supplier_id).catch(() => ({ data: null })),
    ]);
    setPreview({ ...r.data, supplier_name: row.supplier_name, uncovered: u.data });
  };

  const openPreview = async (row) => {
    setPreviewing(true);
    setPreviewRow(row);
    try {
      await loadPreview(row, poIncludes[row.supplier_id] || []);
    } catch {
      toast.error('Preview failed');
    } finally { setPreviewing(false); }
  };

  // Toggle an out-of-window order into/out of "Add to this PO", then re-preview
  // so the aggregated totals reflect the change.
  const toggleIncludeInPO = async (supplierId, orderId) => {
    const current = poIncludes[supplierId] || [];
    const next = current.includes(orderId)
      ? current.filter((id) => id !== orderId)
      : [...current, orderId];
    setPoIncludes((prev) => ({ ...prev, [supplierId]: next }));
    if (previewRow && previewRow.supplier_id === supplierId) {
      setPreviewing(true);
      try { await loadPreview(previewRow, next); }
      catch { toast.error('Preview refresh failed'); }
      finally { setPreviewing(false); }
    }
  };

  const refreshAlert = () =>
    adminAPI.poOutOfWindowAlert().then((r) => setAlert(r.data)).catch(() => {});

  const markOrdered = async (supplierId, orderIds, note = '') => {
    try {
      await adminAPI.markPOOrdersOrdered(supplierId, orderIds, note);
      const u = await adminAPI.getUncoveredOrders(supplierId).catch(() => ({ data: null }));
      setPreview((p) => (p ? { ...p, uncovered: u.data } : p));
      await refreshAlert();
      toast.success(`Marked ${orderIds.length} order${orderIds.length === 1 ? '' : 's'} as ordered`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark as ordered');
    }
  };

  const unmarkOrdered = async (supplierId, orderIds) => {
    try {
      await adminAPI.unmarkPOOrdersOrdered(supplierId, orderIds);
      const u = await adminAPI.getUncoveredOrders(supplierId).catch(() => ({ data: null }));
      setPreview((p) => (p ? { ...p, uncovered: u.data } : p));
      await refreshAlert();
      toast.success('Moved back to “to order”');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to undo');
    }
  };

  const openSend = (row, poType = 'fulfilment') => {
    const isSC = poType === 'stock_check';
    const includeIds = poIncludes[row.supplier_id] || [];
    if (!isSC && !row.totals.item_count && includeIds.length === 0) {
      toast.error('No outstanding lines to send for this supplier');
      return;
    }
    const close = row.window_close_date ? fmtDate(row.window_close_date) : '';
    setSendDialog({
      supplier_id: row.supplier_id,
      supplier_name: row.supplier_name,
      po_type: poType,
      include_order_ids: isSC ? [] : includeIds,
      to: row.contact_email || '',
      cc: '',
      subject: isSC
        ? `AVZ Wholesale Stock Check — ${row.supplier_name}${close ? ` — window closes ${close}` : ''}`
        : `AVZ Wholesale PO — ${row.supplier_name}${close ? ` — window closes ${close}` : ''}`,
      message: isSC
        ? `Hi ${row.supplier_name} team,\n\nAhead of our upcoming order window, please confirm whether you have stock available for the provisional quantities in the attached stock check. These figures are based on current orders (including some not yet finalised) and may change — this is not a confirmed purchase order.\n\nThank you,\nAVZ Wholesale`
        : `Hi ${row.supplier_name} team,\n\nPlease find attached our purchase order for this month. Kindly confirm receipt and your expected dispatch date by reply.\n\nThank you,\nAVZ Wholesale`,
      mark_orders_ordered: !isSC,
    });
  };

  const handleSend = async () => {
    if (!sendDialog) return;
    if (!sendDialog.to.trim()) { toast.error('Recipient email is required'); return; }
    if (!sendDialog.subject.trim()) { toast.error('Subject is required'); return; }
    setSending(true);
    try {
      const cc = sendDialog.cc.split(',').map(s => s.trim()).filter(Boolean);
      const res = await adminAPI.sendPO(sendDialog.supplier_id, {
        to: sendDialog.to.trim(),
        cc,
        subject: sendDialog.subject,
        message: sendDialog.message,
        mark_orders_ordered: sendDialog.mark_orders_ordered,
        po_type: sendDialog.po_type || 'fulfilment',
        include_order_ids: sendDialog.include_order_ids || [],
      });
      if (res.data.status === 'sent') {
        const label = res.data.po_type === 'stock_check' ? 'Stock check' : 'PO';
        const added = (sendDialog.include_order_ids || []).length;
        toast.success(`${label} ${res.data.po_number} sent to ${res.data.sent_to}` +
          (added ? ` · incl. ${added} out-of-window order(s)` : '') +
          (res.data.orders_marked_ordered ? ` · ${res.data.orders_marked_ordered} order(s) → Ordered from Supplier` : ''));
      } else {
        toast.error(`PO ${res.data.po_number} FAILED to send` + (res.data.email_error ? `: ${res.data.email_error}` : ''), { duration: 8000 });
      }
      // clear the include selection for this supplier now that it's sent
      setPoIncludes((prev) => { const n = { ...prev }; delete n[sendDialog.supplier_id]; return n; });
      setSendDialog(null);
      setPreview(null);
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const openHistory = async (row) => {
    setHistoryDialog({ supplier_id: row.supplier_id, supplier_name: row.supplier_name, items: null });
    try {
      const r = await adminAPI.poHistory(row.supplier_id);
      setHistoryDialog((h) => h && { ...h, items: r.data });
    } catch {
      toast.error('Failed to load history');
      setHistoryDialog(null);
    }
  };

  const openResend = (po) => {
    setResendDialog({
      po_id: po.id,
      po_number: po.po_number,
      to: po.sent_to || '',
      cc: (po.sent_cc || []).join(', '),
      subject: po.subject || `AVZ Wholesale PO — ${po.po_number}`,
      message: po.message || '',
    });
  };

  const handleResend = async () => {
    if (!resendDialog) return;
    if (!resendDialog.to.trim()) { toast.error('Recipient email is required'); return; }
    setResending(true);
    try {
      const cc = resendDialog.cc.split(',').map(s => s.trim()).filter(Boolean);
      const res = await adminAPI.resendPO(resendDialog.po_id, {
        to: resendDialog.to.trim(),
        cc,
        subject: resendDialog.subject,
        message: resendDialog.message,
      });
      toast.success(`PO ${res.data.po_number} re-sent to ${res.data.sent_to}`);
      setResendDialog(null);
      // Refresh the open history list + supplier summaries so status flips reflect.
      if (historyDialog?.supplier_id) {
        adminAPI.poHistory(historyDialog.supplier_id)
          .then((r) => setHistoryDialog((h) => h && { ...h, items: r.data }))
          .catch(() => {});
      }
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed', { duration: 8000 });
    } finally { setResending(false); }
  };

  const downloadPO = async (poId, poNumber) => {
    try {
      const res = await adminAPI.downloadPOPdf(poId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${poNumber}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const downloadProvisional = async (row, fmt) => {
    if (row.totals.item_count === 0) { toast.error('No orders to include for this supplier'); return; }
    try {
      toast.message(`Preparing provisional PO (${fmt.toUpperCase()})…`);
      const res = fmt === 'excel'
        ? await adminAPI.downloadProvisionalPOExcel(row.supplier_id)
        : await adminAPI.downloadProvisionalPOPdf(row.supplier_id);
      const type = fmt === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const ext = fmt === 'excel' ? 'xlsx' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement('a');
      a.href = url; a.download = `Provisional_PO_${row.supplier_name.replace(/\s+/g, '_')}.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Provisional PO download failed'); }
  };

  const reconcilePO = async (poId, poNumber) => {
    try {
      const res = await adminAPI.exportPOReconciliation(poId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `PO_Reconciliation_${poNumber}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Reconciliation exported');
    } catch { toast.error('Reconciliation export failed'); }
  };

  const confirmStockForSupplier = async (row) => {
    if (!window.confirm(`Confirm stock & request payment for all awaiting "${row.supplier_name}" orders?\n\nEvery stock-confirm order for this supplier that hasn't been invoiced yet will be sent a payment link (email & WhatsApp), starting their pay-by clock.`)) return;
    try {
      const res = await adminAPI.confirmStockRequestPayment({ supplier_id: row.supplier_id });
      const u = (res.data.updated || []).length;
      if (u) toast.success(`${u} order${u > 1 ? 's' : ''} — payment requested for ${row.supplier_name}`);
      else toast.info('No orders awaiting stock confirmation for this supplier');
      fetchRows();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  const cancelMoqForSupplier = async (row) => {
    if (!window.confirm(`Cancel all awaiting "${row.supplier_name}" orders — minimum not reached?\n\nEvery stock-confirm order for this supplier still awaiting confirmation will be cancelled with a "minimum not reached" notice (no payment was taken). This cannot be undone.`)) return;
    try {
      const res = await adminAPI.cancelMoqNotMet({ supplier_id: row.supplier_id });
      const c = (res.data.cancelled || []).length;
      if (c) toast.success(`${c} order${c > 1 ? 's' : ''} cancelled for ${row.supplier_name}`);
      else toast.info('No orders awaiting stock confirmation for this supplier');
      fetchRows();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  // Group by VENDOR ACTIVE STATE — matches Suppliers/Order Windows pages.
  // Active = is_active !== false AND window is currently open.
  const windowState = (r) => {
    const open = r.window_open_date || '';
    const close = r.window_close_date || '';
    if (!open || !close) return null;
    const now = new Date().toISOString();
    if (now < open) return 'upcoming';
    if (now > close) return 'closed';
    return 'open';
  };
  const isActiveVendor = (r) => r.is_active !== false && windowState(r) === 'open';
  const q = search.trim().toLowerCase();
  const matchesSearch = (r) =>
    !q ||
    (r.supplier_name || '').toLowerCase().includes(q) ||
    (r.contact_email || '').toLowerCase().includes(q);
  const filteredRows = rows.filter(matchesSearch);
  const activeRows = filteredRows.filter(isActiveVendor);
  const inactiveRows = filteredRows.filter((r) => !isActiveVendor(r));

  return (
    <div data-testid="admin-purchase-orders">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Purchase Orders</h1>
          <p className="font-body text-sm text-foreground/70 mt-1">
            {activeRows.length} active vendor{activeRows.length === 1 ? '' : 's'} · {rows.length} total
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40 pointer-events-none" />
          <Input
            data-testid="po-vendor-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendors by name or email…"
            className="rounded-full bg-muted border-border pl-9 pr-9 font-body text-sm"
          />
          {search && (
            <button
              type="button"
              data-testid="po-vendor-search-clear"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Out-of-window uncovered-orders alert (toggleable) */}
      <div className="flex items-center justify-end mb-2">
        <label className="flex items-center gap-2 cursor-pointer" title="Show a warning when any vendor has paid orders outside their window that aren't on a PO">
          <span className="font-body text-[11px] text-foreground/50">Out-of-window PO alert</span>
          <Switch data-testid="po-oow-alert-toggle" checked={alertEnabled} onCheckedChange={toggleAlert} />
        </label>
      </div>
      {alertEnabled && alert && alert.totals?.vendor_count > 0 && (
        <div data-testid="po-oow-alert-banner" className="rounded-2xl border border-orange-500/40 bg-orange-500/10 p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-body text-sm font-semibold text-orange-200">
                {alert.totals.order_count} paid order{alert.totals.order_count === 1 ? '' : 's'} across {alert.totals.vendor_count} vendor{alert.totals.vendor_count === 1 ? '' : 's'} are OUTSIDE their order window and not on any PO ({fmtR(alert.totals.value)})
              </p>
              <p className="font-body text-xs text-orange-200/70 mt-0.5">
                These won't be captured by a normal PO send — open the vendor's Preview to review and include them.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {alert.vendors.map((v) => (
                  <span key={v.supplier_id} data-testid={`po-oow-vendor-${v.supplier_id}`} className="px-2 py-0.5 rounded-full text-[11px] font-body bg-orange-500/20 text-orange-200">
                    {v.supplier_name} · {v.count} · {fmtR(v.value)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Diagnose box — "Why isn't this order on the PO?" */}
      <div className="rounded-2xl bg-card border border-border p-4 mb-6" data-testid="po-diagnose-box">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <Search className="h-4 w-4 text-foreground/40 flex-shrink-0" />
            <Input
              data-testid="po-diagnose-input"
              value={diagQuery}
              onChange={(e) => setDiagQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runDiagnose(); }}
              placeholder="Why isn't this order on the PO?  Enter an order number, e.g. AVZ-2026-00202"
              className="rounded-full bg-muted border-border font-body text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="po-diagnose-btn" onClick={runDiagnose} size="sm" className="rounded-full text-xs gap-1.5 bg-teal-dark border border-transparent text-white hover:bg-teal">
              Diagnose
            </Button>
            {diagResult && (
              <Button variant="outline" size="sm" onClick={() => { setDiagResult(null); setDiagQuery(''); }} className="rounded-full text-xs">Clear</Button>
            )}
          </div>
        </div>
        {diagResult && <DiagnoseResult result={diagResult} />}
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-24 rounded-2xl bg-muted animate-pulse" />)}</div>
      ) : (
        <div className="space-y-8">
          {activeRows.length === 0 && inactiveRows.length === 0 && (
            <div className="py-16 text-center" data-testid="po-no-results">
              <p className="font-body text-sm text-foreground/60">
                No vendors match “{search}”.
              </p>
            </div>
          )}
          {activeRows.length > 0 && (
            <section data-testid="po-section-active">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                <h2 className="font-body text-xs font-bold uppercase tracking-[0.18em] text-foreground">Active Vendors</h2>
                <span className="font-body text-xs text-foreground/40">({activeRows.length})</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-2">
                {activeRows.map((r) => (
                  <SupplierRow
                    key={r.supplier_id}
                    row={r}
                    wState="open"
                    onPreview={() => openPreview(r)}
                    onSend={() => openSend(r)}
                    onStockCheck={() => openSend(r, 'stock_check')}
                    onHistory={() => openHistory(r)}
                    onProvisionalPdf={() => downloadProvisional(r, 'pdf')}
                    onProvisionalExcel={() => downloadProvisional(r, 'excel')}
                    onConfirmStock={() => confirmStockForSupplier(r)}
                    onCancelMoq={() => cancelMoqForSupplier(r)}
                  />
                ))}
              </div>
            </section>
          )}

          {inactiveRows.length > 0 && (
            <section data-testid="po-section-inactive">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-2 w-2 rounded-full bg-foreground/30" />
                <h2 className="font-body text-xs font-bold uppercase tracking-[0.18em] text-foreground/60">Inactive Vendors</h2>
                <span className="font-body text-xs text-foreground/40">({inactiveRows.length})</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-2">
                {inactiveRows.map((r) => (
                  <SupplierRow
                    key={r.supplier_id}
                    row={r}
                    wState={windowState(r)}
                    onPreview={() => openPreview(r)}
                    onSend={() => openSend(r)}
                    onStockCheck={() => openSend(r, 'stock_check')}
                    onHistory={() => openHistory(r)}
                    onProvisionalPdf={() => downloadProvisional(r, 'pdf')}
                    onProvisionalExcel={() => downloadProvisional(r, 'excel')}
                    onConfirmStock={() => confirmStockForSupplier(r)}
                    onCancelMoq={() => cancelMoqForSupplier(r)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* ===== PREVIEW DIALOG ===== */}
      <Dialog open={!!preview || previewing} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="bg-background rounded-2xl max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="po-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              PO Preview — {preview?.supplier_name || '...'}
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-foreground/60">
              Aggregated demand for this supplier across all open customer orders.
            </DialogDescription>
          </DialogHeader>
          {previewing && <Loader2 className="h-6 w-6 animate-spin text-teal mx-auto my-12" />}
          {preview && !previewing && (
            <PreviewBody
              preview={preview}
              onMark={markOrdered}
              onUnmark={unmarkOrdered}
              includeIds={poIncludes[preview?.supplier?.id] || []}
              onToggleInclude={(orderId) => toggleIncludeInPO(preview.supplier.id, orderId)}
            />
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPreview(null)} className="rounded-full" data-testid="po-preview-close">Close</Button>
            {preview && !previewing && (
              <Button
                data-testid="po-preview-send-btn"
                disabled={!(preview.totals?.item_count > 0)}
                onClick={() => {
                  const row = previewRow || {
                    supplier_id: preview.supplier?.id,
                    supplier_name: preview.supplier_name || preview.supplier?.name,
                    contact_email: preview.supplier?.contact_email || '',
                    window_close_date: preview.supplier?.window_close_date || '',
                    totals: preview.totals || {},
                  };
                  setPreview(null);
                  openSend({ ...row, totals: preview.totals || row.totals }, 'fulfilment');
                }}
                className="rounded-full bg-teal text-background hover:bg-teal/90"
              >
                Send PO{(poIncludes[preview?.supplier?.id] || []).length ? ` (incl. ${(poIncludes[preview.supplier.id] || []).length} added)` : ''}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== SEND DIALOG ===== */}
      <Dialog open={!!sendDialog} onOpenChange={(o) => { if (!o) setSendDialog(null); }}>
        <DialogContent className="bg-background rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="po-send-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {sendDialog?.po_type === 'stock_check' ? 'Email Stock Check to' : 'Email PO to'} {sendDialog?.supplier_name}
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-foreground/60">
              {sendDialog?.po_type === 'stock_check'
                ? 'A PROVISIONAL stock-check PDF (including unpaid orders) is attached. It does not confirm the order or change any order statuses.'
                : 'A PDF Purchase Order is attached automatically. The recipient and message are editable below.'}
            </DialogDescription>
          </DialogHeader>
          {sendDialog && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">To *</Label>
                <Input
                  data-testid="po-to"
                  value={sendDialog.to}
                  onChange={(e) => setSendDialog((d) => ({ ...d, to: e.target.value }))}
                  className="rounded-xl bg-muted border-border mt-1.5"
                  placeholder="supplier@example.com"
                />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">CC (comma-separated)</Label>
                <Input
                  data-testid="po-cc"
                  value={sendDialog.cc}
                  onChange={(e) => setSendDialog((d) => ({ ...d, cc: e.target.value }))}
                  className="rounded-xl bg-muted border-border mt-1.5"
                  placeholder="optional"
                />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Subject *</Label>
                <Input
                  data-testid="po-subject"
                  value={sendDialog.subject}
                  onChange={(e) => setSendDialog((d) => ({ ...d, subject: e.target.value }))}
                  className="rounded-xl bg-muted border-border mt-1.5"
                />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Message</Label>
                <Textarea
                  data-testid="po-message"
                  rows={6}
                  value={sendDialog.message}
                  onChange={(e) => setSendDialog((d) => ({ ...d, message: e.target.value }))}
                  className="rounded-xl bg-muted border-border mt-1.5 font-body text-sm"
                />
              </div>
              {sendDialog.po_type !== 'stock_check' && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
                  <Switch
                    data-testid="po-mark-ordered"
                    checked={sendDialog.mark_orders_ordered}
                    onCheckedChange={(v) => setSendDialog((d) => ({ ...d, mark_orders_ordered: v }))}
                  />
                  <span className="font-body text-sm text-foreground/80">
                    Mark included customer orders as <strong>Ordered from Supplier</strong> after sending
                  </span>
                </div>
              )}
              <div className="flex items-start gap-2 text-foreground/60 text-xs font-body">
                <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>The PDF Purchase Order will be attached automatically. A copy is stored on this supplier's history for re-download.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(null)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="po-send-btn"
              onClick={handleSend}
              disabled={sending}
              className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light gap-2"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {sendDialog?.po_type === 'stock_check' ? 'Send Stock Check' : 'Send PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== HISTORY DIALOG ===== */}
      <Dialog open={!!historyDialog} onOpenChange={(o) => { if (!o) setHistoryDialog(null); }}>
        <DialogContent className="bg-background rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="po-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              PO History — {historyDialog?.supplier_name}
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-foreground/60">
              Every Purchase Order ever sent to this supplier. Click PDF to re-download the exact file emailed.
            </DialogDescription>
          </DialogHeader>
          {!historyDialog?.items ? (
            <Loader2 className="h-6 w-6 animate-spin text-teal mx-auto my-8" />
          ) : historyDialog.items.length === 0 ? (
            <p className="font-body text-sm text-foreground/50 py-8 text-center">No POs sent to this supplier yet.</p>
          ) : (
            <div className="space-y-2">
              {historyDialog.items.map((po) => (
                <div key={po.id} data-testid={`po-history-row-${po.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border">
                  <div className={`h-2.5 w-2.5 rounded-full ${po.status === 'sent' ? 'bg-green-400' : 'bg-red-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-body font-bold text-foreground text-sm flex items-center gap-2">
                      {po.po_number}
                      {po.po_type === 'stock_check' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gold/15 text-gold" data-testid={`po-type-badge-${po.id}`}>
                          STOCK CHECK
                        </span>
                      )}
                    </p>
                    <p className="font-body text-xs text-foreground/60">
                      {fmtDT(po.sent_at)} → {po.sent_to} · {po.totals?.item_count || 0} items · {fmtR(po.totals?.grand_total || 0)}
                    </p>
                  </div>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => downloadPO(po.id, po.po_number)}
                    className="rounded-full text-xs gap-1.5"
                  >
                    <Download className="h-3 w-3" /> PDF
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    data-testid={`po-resend-${po.id}`}
                    onClick={() => openResend(po)}
                    className="rounded-full text-xs gap-1.5 border-gold/40 text-gold hover:bg-gold/10"
                    title="Re-email this exact PO to the supplier"
                  >
                    <Send className="h-3 w-3" /> Resend
                  </Button>
                  {po.status === 'sent' && po.po_type !== 'stock_check' && (
                    <Button
                      variant="outline" size="sm"
                      data-testid={`po-reconcile-${po.id}`}
                      onClick={() => reconcilePO(po.id, po.po_number)}
                      className="rounded-full text-xs gap-1.5 border-teal/40 text-teal hover:bg-teal/10"
                    >
                      <FileText className="h-3 w-3" /> Reconcile
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialog(null)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== RESEND DIALOG ===== */}
      <Dialog open={!!resendDialog} onOpenChange={(o) => { if (!o) setResendDialog(null); }}>
        <DialogContent className="bg-background rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="po-resend-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              Resend PO {resendDialog?.po_number}
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-foreground/60">
              Re-emails the exact same PDF that was originally sent. No orders are re-aggregated or changed.
            </DialogDescription>
          </DialogHeader>
          {resendDialog && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">To *</Label>
                <Input
                  data-testid="po-resend-to"
                  value={resendDialog.to}
                  onChange={(e) => setResendDialog((d) => ({ ...d, to: e.target.value }))}
                  className="rounded-xl bg-muted border-border mt-1.5"
                  placeholder="supplier@example.com"
                />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">CC (comma-separated)</Label>
                <Input
                  data-testid="po-resend-cc"
                  value={resendDialog.cc}
                  onChange={(e) => setResendDialog((d) => ({ ...d, cc: e.target.value }))}
                  className="rounded-xl bg-muted border-border mt-1.5"
                  placeholder="optional"
                />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Subject *</Label>
                <Input
                  data-testid="po-resend-subject"
                  value={resendDialog.subject}
                  onChange={(e) => setResendDialog((d) => ({ ...d, subject: e.target.value }))}
                  className="rounded-xl bg-muted border-border mt-1.5"
                />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Message</Label>
                <Textarea
                  data-testid="po-resend-message"
                  rows={6}
                  value={resendDialog.message}
                  onChange={(e) => setResendDialog((d) => ({ ...d, message: e.target.value }))}
                  className="rounded-xl bg-muted border-border mt-1.5 font-body text-sm"
                />
              </div>
              <div className="flex items-start gap-2 text-foreground/60 text-xs font-body">
                <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>The original stored PDF is attached automatically. The PO number stays the same.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendDialog(null)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="po-resend-btn"
              onClick={handleResend}
              disabled={resending}
              className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light gap-2"
            >
              {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Resend PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SupplierRow({ row, wState, onPreview, onSend, onStockCheck, onHistory, onProvisionalPdf, onProvisionalExcel, onConfirmStock, onCancelMoq }) {
  const sent = row.last_po && row.last_po.status === 'sent';
  const failed = row.last_po && row.last_po.status === 'failed';
  const hasLines = row.totals.item_count > 0;
  const isTypeB = row.payment_workflow === 'after_confirmation';
  return (
    <div
      data-testid={`po-card-${row.supplier_id}`}
      className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 sm:p-5 rounded-2xl bg-card border border-border hover:border-teal/30 transition-colors"
    >
      {/* Left — avatar + identity */}
      <div className="flex items-center gap-3 lg:w-[280px] flex-shrink-0">
        {row.image_url ? (
          <img
            src={row.image_url}
            alt={row.supplier_name}
            className="w-12 h-12 rounded-xl object-contain bg-white/5 flex-shrink-0"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fb = e.currentTarget.nextElementSibling;
              if (fb) fb.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`w-12 h-12 rounded-xl bg-teal/10 items-center justify-center flex-shrink-0 ${row.image_url ? 'hidden' : 'flex'}`}>
          <span className="font-heading text-lg text-teal">{row.supplier_name[0]}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-body text-base font-semibold text-foreground truncate">{row.supplier_name}</h3>
          <p className="font-body text-xs text-foreground/50 truncate">{row.contact_email || '— no contact email —'}</p>
          {row.window_close_date && (
            <p className="font-body text-[11px] text-foreground/40 mt-0.5">
              {fmtDate(row.window_open_date)} — {fmtDate(row.window_close_date)}
            </p>
          )}
          {row.window_half_configured && (
            <p
              data-testid={`po-window-warning-${row.supplier_id}`}
              className="flex items-center gap-1 font-body text-[11px] text-amber-400 mt-1"
              title="Only one of the open/close window dates is set. No date filter is applied, so all paid orders are included."
            >
              <AlertTriangle className="h-3 w-3 flex-shrink-0" />
              Order window is only half-configured
            </p>
          )}
        </div>
      </div>

      {/* Middle — inline metrics */}
      <div className="grid grid-cols-3 gap-2 lg:flex-1">
        <Metric label="Items" value={row.totals.item_count} testid={`po-items-${row.supplier_id}`} />
        <Metric label="Units" value={row.totals.unit_count} testid={`po-units-${row.supplier_id}`} />
        <Metric label="Total" value={fmtR(row.totals.grand_total)} testid={`po-total-${row.supplier_id}`} />
      </div>

      {/* Right — last PO + actions */}
      <div className="flex flex-col gap-2 lg:items-start lg:w-[440px] flex-shrink-0">
        {row.last_po && (
          <div
            className={`flex items-center gap-2 text-[11px] font-body px-2.5 py-1.5 rounded-lg w-fit ${
              sent ? 'bg-green-900/20 text-green-300' : failed ? 'bg-red-900/20 text-red-300' : 'bg-muted'
            }`}
            data-testid={`po-last-${row.supplier_id}`}
          >
            {sent && <CheckCircle2 className="h-3.5 w-3.5" />}
            {failed && <AlertTriangle className="h-3.5 w-3.5" />}
            <span>
              {row.last_po.po_number} · {fmtDT(row.last_po.sent_at)} · {row.last_po.status}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap lg:justify-start">
          <Button
            data-testid={`po-preview-${row.supplier_id}`}
            variant="outline"
            size="sm"
            onClick={onPreview}
            className="rounded-full text-xs gap-1.5 border-border text-foreground/80 hover:border-teal/50 hover:text-foreground"
          >
            <Eye className="h-3.5 w-3.5" /> Preview
          </Button>
          <Button
            data-testid={`po-send-${row.supplier_id}`}
            size="sm"
            onClick={onSend}
            disabled={!hasLines || !row.contact_email}
            className="rounded-full text-xs gap-1.5 bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light"
            title={!row.contact_email ? 'Supplier has no contact email' : !hasLines ? 'No outstanding lines' : 'Email PO'}
          >
            <Mail className="h-3.5 w-3.5" /> Email PO
          </Button>
          <Button
            data-testid={`po-history-${row.supplier_id}`}
            variant="outline"
            size="sm"
            onClick={onHistory}
            className="rounded-full text-xs gap-1.5 border-border text-foreground/80 hover:border-teal/50 hover:text-foreground"
          >
            <History className="h-3.5 w-3.5" /> History
          </Button>
          <Button
            data-testid={`po-stockcheck-${row.supplier_id}`}
            variant="outline"
            size="sm"
            onClick={onStockCheck}
            disabled={!row.contact_email}
            className="rounded-full text-xs gap-1.5 border-border text-foreground/80 hover:border-teal/50 hover:text-foreground"
            title={!row.contact_email ? 'Supplier has no contact email' : 'Send a provisional stock-check PO (includes unpaid orders; does not affect the real PO)'}
          >
            <AlertTriangle className="h-3.5 w-3.5" /> Stock Check
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={!hasLines}>
              <Button
                data-testid={`po-provisional-${row.supplier_id}`}
                variant="outline"
                size="sm"
                disabled={!hasLines}
                className="rounded-full text-xs gap-1.5 border-border text-foreground/80 hover:border-teal/50 hover:text-foreground"
                title={hasLines ? 'Download a provisional PO (paid + unpaid orders)' : 'No orders to include'}
              >
                <FileText className="h-3.5 w-3.5" /> Provisional PO <ChevronDown className="h-3 w-3 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-card border-border">
              <DropdownMenuItem
                data-testid={`po-provisional-pdf-${row.supplier_id}`}
                onClick={onProvisionalPdf}
                className="gap-2 cursor-pointer"
              >
                <FileText className="h-4 w-4 text-gold" /> Export to PDF
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`po-provisional-excel-${row.supplier_id}`}
                onClick={onProvisionalExcel}
                className="gap-2 cursor-pointer"
              >
                <FileSpreadsheet className="h-4 w-4 text-gold" /> Export to Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {isTypeB && (
          <div className="flex items-center gap-2 flex-wrap lg:justify-start">
            <Button
              data-testid={`po-confirm-stock-${row.supplier_id}`}
              size="sm"
              onClick={onConfirmStock}
              className="rounded-full text-xs gap-1.5 bg-teal/15 border border-teal/70 text-teal hover:bg-teal-dark hover:text-white hover:border-teal-dark font-semibold"
              title="Confirm stock and send the payment link to every awaiting order for this supplier"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Confirm Stock &amp; Request Payment
            </Button>
            <Button
              data-testid={`po-cancel-moq-${row.supplier_id}`}
              size="sm"
              onClick={onCancelMoq}
              className="rounded-full text-xs gap-1.5 bg-red-500/15 border border-red-400/70 text-red-300 hover:bg-red-700 hover:text-white hover:border-red-700 font-semibold"
              title="Cancel every awaiting order for this supplier because the minimum wasn't reached"
            >
              <X className="h-3.5 w-3.5" /> Cancel — MOQ Not Met
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="px-3 py-2 rounded-xl bg-muted/40 border border-border" data-testid={testid}>
      <p className="font-body text-[10px] uppercase tracking-wider text-foreground/40">{label}</p>
      <p className="font-body text-sm font-bold text-foreground mt-0.5">{value}</p>
    </div>
  );
}

function DiagnoseResult({ result }) {
  if (result.loading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-foreground/60" data-testid="po-diagnose-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> <span className="font-body text-xs">Checking…</span>
      </div>
    );
  }
  if (result.error) {
    return (
      <div className="mt-3 flex items-center gap-2 text-red-300" data-testid="po-diagnose-error">
        <X className="h-4 w-4" /> <span className="font-body text-xs">{result.error}</span>
      </div>
    );
  }
  const suppliers = result.suppliers || [];
  const orphans = result.orphan_lines || [];
  return (
    <div className="mt-3 space-y-2" data-testid="po-diagnose-result">
      <div className="flex flex-wrap items-center gap-2 font-body text-xs text-foreground/70">
        <span className="font-semibold text-foreground">{result.order_number}</span>
        <span className={`px-1.5 py-0.5 rounded ${result.order_is_paid ? 'bg-green-500/15 text-green-300' : 'bg-orange-500/15 text-orange-300'}`}>
          {result.payment_status}
        </span>
        <span className="px-1.5 py-0.5 rounded bg-muted text-foreground/70">{result.status}</span>
        <span className="text-foreground/40">{(result.created_at || '').slice(0, 10)}</span>
      </div>
      {suppliers.map((s) => (
        <div key={s.supplier_id} data-testid={`po-diagnose-supplier-${s.supplier_id}`} className={`rounded-xl border p-3 ${s.would_be_included ? 'border-green-500/30 bg-green-500/5' : 'border-orange-500/30 bg-orange-500/5'}`}>
          <div className="flex items-center gap-2 mb-1">
            {s.would_be_included
              ? <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
              : <AlertTriangle className="h-4 w-4 text-orange-400 flex-shrink-0" />}
            <span className="font-body text-sm font-semibold text-foreground">{s.supplier_name}</span>
            <span className={`font-body text-xs ${s.would_be_included ? 'text-green-300' : 'text-orange-300'}`}>
              {s.would_be_included ? 'will be included on the next PO' : 'NOT on the PO'}
            </span>
          </div>
          {!s.would_be_included && s.exclusion_reasons?.length > 0 && (
            <ul className="list-disc list-inside font-body text-xs text-orange-200/80 space-y-0.5">
              {s.exclusion_reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
          {s.lines?.some((l) => l.product_exists === false) && (
            <p className="font-body text-[11px] text-foreground/50 mt-1">
              Note: some products were deleted/re-created — attributed via the supplier captured on the order line.
            </p>
          )}
        </div>
      ))}
      {orphans.length > 0 && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3" data-testid="po-diagnose-orphans">
          <p className="font-body text-xs font-semibold text-red-300 mb-1">{orphans.length} line(s) can't be attributed to any supplier</p>
          <ul className="list-disc list-inside font-body text-xs text-red-200/80 space-y-0.5">
            {orphans.map((o, i) => <li key={i}>{o.name} — {o.reason_excluded}</li>)}
          </ul>
        </div>
      )}
      {suppliers.length === 0 && orphans.length === 0 && (
        <p className="font-body text-xs text-foreground/50">This order has no line items for any supplier.</p>
      )}
    </div>
  );
}

function UncoveredPanel({ uncovered, onMark, onUnmark, includeIds = [], onToggleInclude }) {
  const [busy, setBusy] = useState(false);
  if (!uncovered) return null;
  const { uncovered_orders: orders = [], acknowledged_orders: acked = [], totals = {}, supplier_id: supplierId } = uncovered;
  const outCount = totals.out_of_window_count || 0;
  const outOrders = orders.filter((o) => !o.in_current_window);
  const includeSet = new Set(includeIds);
  const addedCount = outOrders.filter((o) => includeSet.has(o.order_id)).length;
  const allOutAdded = outOrders.length > 0 && outOrders.every((o) => includeSet.has(o.order_id));

  const toggleAllToPO = () => {
    // add all if not all added, else remove all
    outOrders.forEach((o) => {
      const isIn = includeSet.has(o.order_id);
      if (allOutAdded ? isIn : !isIn) onToggleInclude?.(o.order_id);
    });
  };
  const doMark = async (id) => {
    setBusy(true);
    try { if (includeSet.has(id)) onToggleInclude?.(id); await onMark?.(supplierId, [id]); }
    finally { setBusy(false); }
  };
  const doUnmark = async (id) => {
    setBusy(true);
    try { await onUnmark?.(supplierId, [id]); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      {!totals.count ? (
        <div
          data-testid="po-uncovered-none"
          className="flex items-center gap-2 rounded-xl border border-green-500/30 bg-green-500/10 px-3 py-2.5"
        >
          <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
          <p className="font-body text-xs text-green-200/90">
            Every paid order for this vendor is already on a PO or marked as ordered. Nothing outstanding.
          </p>
        </div>
      ) : (
        <div data-testid="po-uncovered-panel" className="rounded-xl border border-orange-500/40 bg-orange-500/10 p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-orange-400 flex-shrink-0" />
            <p className="font-body text-sm font-semibold text-orange-200">
              {totals.count} paid order{totals.count === 1 ? '' : 's'} not yet on a PO · {fmtR(totals.value)}
            </p>
          </div>
          <p className="font-body text-[11px] text-orange-200/70 mb-2.5">
            {totals.in_window_count || 0} in the current window (included when you send this PO)
            {outCount > 0 && (
              <span className="text-orange-300 font-semibold"> · {outCount} OUTSIDE the current window — tick “Add to PO” to include them, or mark as already ordered</span>
            )}
          </p>
          {outCount > 0 && (
            <div className="flex items-center justify-between gap-2 mb-1.5 pb-1.5 border-b border-orange-500/20">
              <label className="flex items-center gap-1.5 cursor-pointer font-body text-[11px] text-orange-200/90 font-semibold">
                <Checkbox data-testid="po-oow-addall" checked={allOutAdded} onCheckedChange={toggleAllToPO} />
                Add all out-of-window to this PO
              </label>
              {addedCount > 0 && (
                <span data-testid="po-oow-added-count" className="font-body text-[11px] text-teal font-semibold">
                  {addedCount} added → in PO
                </span>
              )}
            </div>
          )}
          <div className="space-y-1">
            {orders.map((o) => {
              const added = includeSet.has(o.order_id);
              return (
                <div
                  key={o.order_id}
                  data-testid={`po-uncovered-${o.order_number}`}
                  className={`flex items-center gap-3 font-body text-xs py-1.5 px-1 rounded border-b border-orange-500/15 last:border-0 ${added ? 'bg-teal/10' : ''}`}
                >
                  {!o.in_current_window ? (
                    <label className="flex items-center gap-1 cursor-pointer flex-shrink-0" title="Add this order to the PO">
                      <Checkbox
                        data-testid={`po-oow-add-${o.order_number}`}
                        checked={added}
                        onCheckedChange={() => onToggleInclude?.(o.order_id)}
                      />
                    </label>
                  ) : <span className="w-4 flex-shrink-0" />}
                  <span className="font-semibold text-foreground">{o.order_number}</span>
                  <span className="flex-1 truncate text-foreground/60">{o.customer}</span>
                  <span className="text-foreground/50 hidden sm:inline">{(o.created_at || '').slice(0, 10)}</span>
                  {added ? (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-teal/25 text-teal whitespace-nowrap">IN PO</span>
                  ) : !o.in_current_window ? (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-500/20 text-orange-300 whitespace-nowrap">OUT OF WINDOW</span>
                  ) : null}
                  <span className="font-semibold text-orange-300 whitespace-nowrap">{fmtR(o.value)}</span>
                  {!o.in_current_window && (
                    <Button
                      data-testid={`po-mark-ordered-${o.order_number}`}
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => doMark(o.order_id)}
                      className="h-6 rounded-full font-body text-[10px] px-2 text-foreground/50 hover:text-foreground gap-1 flex-shrink-0"
                      title="Already ordered outside the system — just clear it from this list"
                    >
                      <CheckCircle2 className="h-3 w-3" /> Mark ordered
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {acked.length > 0 && (
        <div data-testid="po-acknowledged-panel" className="rounded-xl border border-green-500/25 bg-green-500/5 p-3">
          <p className="font-body text-xs font-semibold text-green-200/90 mb-2 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
            Marked as ordered · {acked.length} · {fmtR(totals.acknowledged_value || 0)}
          </p>
          <div className="space-y-1">
            {acked.map((o) => (
              <div
                key={o.order_id}
                data-testid={`po-acknowledged-${o.order_number}`}
                className="flex items-center justify-between gap-3 font-body text-xs text-foreground/70 py-1 border-b border-green-500/10 last:border-0"
              >
                <span className="font-semibold text-foreground/90">{o.order_number}</span>
                <span className="flex-1 truncate text-foreground/50">{o.customer}</span>
                <span className="text-foreground/40 whitespace-nowrap" title={o.marked_at ? `by ${o.marked_by || 'admin'}` : ''}>
                  {o.marked_at ? fmtDate(o.marked_at) : ''}
                </span>
                <span className="text-foreground/50 whitespace-nowrap">{fmtR(o.value)}</span>
                <Button
                  data-testid={`po-unmark-${o.order_number}`}
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => doUnmark(o.order_id)}
                  className="h-6 rounded-full font-body text-[10px] px-2 text-foreground/50 hover:text-foreground gap-1"
                >
                  <X className="h-3 w-3" /> Undo
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewBody({ preview, onMark, onUnmark, includeIds, onToggleInclude }) {
  return (
    <div className="space-y-4 py-2">
      {preview.window_half_configured && (
        <div
          data-testid="preview-window-warning"
          className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5"
        >
          <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="font-body text-xs text-amber-200/90">
            This supplier's order window is only half-configured (one of the open/close dates is missing).
            No date filter is applied, so <span className="font-semibold">all paid orders</span> are included in this PO.
          </p>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        <Metric label="Items" value={preview.totals.item_count} testid="preview-items" />
        <Metric label="Units" value={preview.totals.unit_count} testid="preview-units" />
        <Metric label="Grand Total" value={fmtR(preview.totals.grand_total)} testid="preview-total" />
      </div>

      <UncoveredPanel uncovered={preview.uncovered} onMark={onMark} onUnmark={onUnmark} includeIds={includeIds} onToggleInclude={onToggleInclude} />

      <div>
        <p className="font-body text-xs uppercase tracking-wider text-foreground/50 mb-2">Aggregated Line Items</p>
        <div className="rounded-xl overflow-hidden border border-border">
          <table className="w-full text-sm font-body">
            <thead className="bg-muted/60 text-foreground/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Size</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-right px-3 py-2">Unit Cost</th>
                <th className="text-right px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {preview.line_items.map((it, idx) => (
                <tr key={idx} className="border-t border-border/50">
                  <td className="px-3 py-2 text-foreground/70">{it.sku || '—'}</td>
                  <td className="px-3 py-2 text-foreground">{it.product_name}</td>
                  <td className="px-3 py-2 text-foreground/70">{it.unit_size || '—'}</td>
                  <td className="px-3 py-2 text-right text-foreground">{it.qty}</td>
                  <td className="px-3 py-2 text-right text-foreground/70">{fmtR(it.unit_cost)}</td>
                  <td className="px-3 py-2 text-right text-foreground font-bold">{fmtR(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {preview.order_numbers && preview.order_numbers.length > 0 && (
        <div data-testid="preview-orders-included">
          <p className="font-body text-xs uppercase tracking-wider text-foreground/50 mb-1">
            Orders Included ({preview.order_numbers.length})
          </p>
          <p className="font-body text-sm text-foreground/80 leading-relaxed break-words">
            {preview.order_numbers.join(', ')}
          </p>
        </div>
      )}

      <div>
        <p className="font-body text-xs uppercase tracking-wider text-foreground/50 mb-2">By Customer</p>
        {preview.customer_breakdown.length === 0 ? (
          <p className="font-body text-sm text-foreground/40 italic">No customer breakdown</p>
        ) : (
          <div className="space-y-3">
            {preview.customer_breakdown.map((c, ci) => (
              <div key={ci} className="rounded-xl border border-border p-3 bg-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-body font-bold text-foreground text-sm">{c.customer_name}</p>
                  <p className="font-body text-sm text-teal font-bold">{fmtR(c.subtotal)}</p>
                </div>
                <div className="space-y-1">
                  {c.items.map((it, ii) => (
                    <div key={ii} className="flex items-center justify-between text-xs font-body text-foreground/70">
                      <span>{it.product_name} <span className="text-foreground/40">({it.sku || '—'}{it.unit_size ? `, ${it.unit_size}` : ''})</span></span>
                      <span>{it.qty} × {fmtR(it.unit_cost)} = <strong className="text-foreground">{fmtR(it.line_total)}</strong></span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
